import { useCallback } from "react";
import { useReactFlow } from "@xyflow/react";
import { getDefinition } from "@/lib/catalog";
import { useCustomLibrary } from "@/lib/custom-library";
import { useSystemStore } from "@/lib/store";
import type { ComponentDefinition, ZoneNode as ZoneNodeT } from "@/lib/types";

const ZONE_DEFAULT_WIDTH = 480;
const ZONE_DEFAULT_HEIGHT = 320;

export function useCanvasDrop() {
  const { screenToFlowPosition } = useReactFlow();
  const system = useSystemStore((s) => s.system);
  const addComponent = useSystemStore((s) => s.addComponent);
  const addZone = useSystemStore((s) => s.addZone);
  const setNodeParent = useSystemStore((s) => s.setNodeParent);
  const setNodePosition = useSystemStore((s) => s.setNodePosition);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const dropType = event.dataTransfer.getData("application/twelve-volt");
      if (!dropType) return;

      const flowPos = screenToFlowPosition({ x: event.clientX, y: event.clientY });

      let resolved:
        | { defId: string; customDef?: ComponentDefinition; width: number; height: number }
        | null = null;

      if (dropType.startsWith("def:")) {
        const defId = dropType.slice(4);
        const def = getDefinition(defId);
        if (!def) return;
        resolved = { defId, width: def.width, height: def.height };
      } else if (dropType.startsWith("lib:")) {
        const libId = dropType.slice(4);
        const entry = useCustomLibrary
          .getState()
          .entries.find((e) => e.libId === libId);
        if (!entry) return;
        resolved = {
          defId: "custom",
          customDef: entry.definition,
          width: entry.definition.width,
          height: entry.definition.height,
        };
      }

      if (resolved) {
        const pos = {
          x: flowPos.x - resolved.width / 2,
          y: flowPos.y - resolved.height / 2,
        };
        const id = addComponent(
          resolved.defId,
          pos,
          resolved.customDef,
          "palette_drag",
        );
        if (!id) return;
        const zones = system.nodes.filter((n): n is ZoneNodeT => n.kind === "zone");
        const containing = zones.find(
          (z) =>
            flowPos.x >= z.position.x &&
            flowPos.x <= z.position.x + z.width &&
            flowPos.y >= z.position.y &&
            flowPos.y <= z.position.y + z.height,
        );
        if (containing) {
          setNodeParent(id, containing.id);
          setNodePosition(id, {
            x: pos.x - containing.position.x,
            y: pos.y - containing.position.y,
          });
        }
      } else if (dropType === "zone") {
        const pos = {
          x: flowPos.x - ZONE_DEFAULT_WIDTH / 2,
          y: flowPos.y - ZONE_DEFAULT_HEIGHT / 2,
        };
        addZone(pos, { width: ZONE_DEFAULT_WIDTH, height: ZONE_DEFAULT_HEIGHT });
      }
    },
    [screenToFlowPosition, addComponent, addZone, system.nodes, setNodeParent, setNodePosition],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  return { onDrop, onDragOver };
}
